import { useEffect, useState } from 'react';
import { useT, itemName } from '@/i18n';
import { getItem } from '@/domain/items';
import { useGameStore } from '@/state/gameStore';
import { useAuthStore } from '@/state/authStore';
import { useMarketStore, useFilteredListings, type ListingSort, type Listing } from '@/state/marketStore';
import { ListingCard } from '@/ui/components/ListingCard';
import { ListingDetailModal } from '@/ui/components/ListingDetailModal';
import { CreateListingModal } from '@/ui/components/CreateListingModal';
import { ItemIcon } from '@/ui/components/ItemIcon';
import type { EquipmentInstance, EquipmentSlot, Rarity } from '@/domain/types';
import './MarketScreen.css';

type SubTab = 'browse' | 'mine' | 'sell';

const RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const SLOTS: EquipmentSlot[] = ['weapon', 'helmet', 'armor', 'accessory'];
const SORTS: Array<{ id: ListingSort; label: string }> = [
  { id: 'newest', label: 'Neueste' },
  { id: 'price_asc', label: 'Günstig zuerst' },
  { id: 'price_desc', label: 'Teuer zuerst' },
];

export function MarketScreen() {
  const t = useT();
  const player = useGameStore((s) => s.player);
  const userId = useAuthStore((s) => s.user?.id);

  const listings = useFilteredListings();
  const myListings = useMarketStore((s) => s.myListings);
  const filterRarity = useMarketStore((s) => s.filterRarity);
  const filterSlot = useMarketStore((s) => s.filterSlot);
  const sort = useMarketStore((s) => s.sort);
  const setFilterRarity = useMarketStore((s) => s.setFilterRarity);
  const setFilterSlot = useMarketStore((s) => s.setFilterSlot);
  const setSort = useMarketStore((s) => s.setSort);
  const loadListings = useMarketStore((s) => s.loadListings);
  const loadMyListings = useMarketStore((s) => s.loadMyListings);
  const subscribeRealtime = useMarketStore((s) => s.subscribeRealtime);
  const buyListing = useMarketStore((s) => s.buyListing);
  const cancelListing = useMarketStore((s) => s.cancelListing);
  const createListing = useMarketStore((s) => s.createListing);
  const loading = useMarketStore((s) => s.loading);
  const error = useMarketStore((s) => s.error);

  const [tab, setTab] = useState<SubTab>('browse');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sellTarget, setSellTarget] = useState<EquipmentInstance | null>(null);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [detailListing, setDetailListing] = useState<Listing | null>(null);

  // Initial load + realtime subscription
  useEffect(() => {
    void loadListings();
    void loadMyListings();
    const unsub = subscribeRealtime();
    return unsub;
  }, [loadListings, loadMyListings, subscribeRealtime]);

  if (!player) return null;

  const flash = (ok: boolean, msg: string) => {
    setFeedback({ ok, msg });
    setTimeout(() => setFeedback(null), 3500);
  };

  const handleBuy = async (listingId: string) => {
    setBusyId(listingId);
    const result = await buyListing(listingId);
    setBusyId(null);
    flash(result.ok, result.ok ? '✓ Gekauft! Item ist im Stash.' : `Kauf fehlgeschlagen: ${result.error}`);
  };

  const handleCancel = async (listingId: string) => {
    if (!confirm('Listing wirklich abbrechen?')) return;
    setBusyId(listingId);
    const result = await cancelListing(listingId);
    setBusyId(null);
    flash(result.ok, result.ok ? '✓ Listing abgebrochen' : `Fehler: ${result.error}`);
  };

  // Items in stash that are NOT yet listed (open) by this user
  const listedInstanceIds = new Set(
    myListings.filter((l) => l.status === 'open').map((l) => l.instanceId),
  );
  const sellableStash = player.equipmentStash.filter((inst) => !listedInstanceIds.has(inst.id));

  const handleStartListing = (instance: EquipmentInstance) => {
    setSellTarget(instance);
  };

  const handleConfirmListing = async (price: number) => {
    if (!sellTarget) return { ok: false, error: 'no_target' };
    const result = await createListing(sellTarget, price);
    if (result.ok) {
      flash(true, '✓ Listing erstellt');
    }
    return result;
  };

  return (
    <div className="screen market-screen">
      <h1 className="screen__headline">{t.market.headline}</h1>

      {/* Sub-Tabs */}
      <div className="market-tabs">
        <button
          className={`market-tabs__btn ${tab === 'browse' ? 'market-tabs__btn--active' : ''}`}
          onClick={() => setTab('browse')}
        >
          📦 Angebote ({listings.length})
        </button>
        <button
          className={`market-tabs__btn ${tab === 'mine' ? 'market-tabs__btn--active' : ''}`}
          onClick={() => setTab('mine')}
        >
          📋 Meine ({myListings.filter((l) => l.status === 'open').length})
        </button>
        <button
          className={`market-tabs__btn ${tab === 'sell' ? 'market-tabs__btn--active' : ''}`}
          onClick={() => setTab('sell')}
        >
          💰 Verkaufen ({sellableStash.length})
        </button>
      </div>

      {feedback && (
        <div className={`market-feedback ${feedback.ok ? 'market-feedback--ok' : 'market-feedback--err'}`}>
          {feedback.msg}
        </div>
      )}

      {error && <div className="market-feedback market-feedback--err">⛔ {error}</div>}

      {/* TAB: BROWSE */}
      {tab === 'browse' && (
        <>
          <div className="market-filters">
            <select
              value={filterRarity ?? ''}
              onChange={(e) => setFilterRarity((e.target.value || null) as Rarity | null)}
            >
              <option value="">Alle Raritäten</option>
              {RARITIES.map((r) => (
                <option key={r} value={r}>{t.inventory.rarity[r]}</option>
              ))}
            </select>
            <select
              value={filterSlot ?? ''}
              onChange={(e) => setFilterSlot((e.target.value || null) as EquipmentSlot | null)}
            >
              <option value="">Alle Slots</option>
              {SLOTS.map((s) => (
                <option key={s} value={s}>{t.character.slots[s]}</option>
              ))}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value as ListingSort)}>
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {loading && <div className="market-empty">Lade …</div>}
          {!loading && listings.length === 0 && (
            <div className="market-empty">Keine Angebote — sei der Erste!</div>
          )}

          <div className="market-list">
            {listings.map((l) => {
              const isOwn = l.sellerId === userId;
              const cantAfford = player.credits < l.price;
              const disabled = isOwn || cantAfford;
              const reason = isOwn ? 'Eigenes Angebot' : cantAfford ? 'Zu wenig Credits' : undefined;
              return (
                <ListingCard
                  key={l.id}
                  listing={l}
                  variant="buy"
                  disabled={disabled}
                  disabledReason={reason}
                  busy={busyId === l.id}
                  onAction={() => handleBuy(l.id)}
                  onOpenDetail={() => setDetailListing(l)}
                />
              );
            })}
          </div>
        </>
      )}

      {/* TAB: MINE */}
      {tab === 'mine' && (
        <>
          {myListings.length === 0 ? (
            <div className="market-empty">Keine eigenen Listings.</div>
          ) : (
            <div className="market-list">
              {myListings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  variant="manage"
                  busy={busyId === l.id}
                  onAction={() => handleCancel(l.id)}
                  onOpenDetail={() => setDetailListing(l)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* TAB: SELL */}
      {tab === 'sell' && (
        <>
          <div className="market-hint">Equipment im Stash antippen um Preis festzulegen und zu listen.</div>
          {sellableStash.length === 0 ? (
            <div className="market-empty">
              Keine ungelisteten Items im Stash. Geh in einen Dungeon, finde Beute und komm wieder.
            </div>
          ) : (
            <div className="market-sell-grid">
              {sellableStash.map((inst) => {
                const def = getItem(inst.itemId);
                if (!def) return null;
                return (
                  <button
                    key={inst.id}
                    className={`sell-item sell-item--${def.rarity}`}
                    onClick={() => handleStartListing(inst)}
                  >
                    <ItemIcon itemId={def.id} sizeRem={2.2} />
                    <span className="sell-item__level">+{inst.upgradeLevel}</span>
                    <span className="sell-item__name">{itemName(def.nameKey)}</span>
                    <span className="sell-item__rarity">{t.inventory.rarity[def.rarity]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {sellTarget && (
        <CreateListingModal
          instance={sellTarget}
          onClose={() => setSellTarget(null)}
          onConfirm={handleConfirmListing}
        />
      )}

      {detailListing && (
        <ListingDetailModal
          listing={detailListing}
          variant={detailListing.sellerId === userId ? 'manage' : 'buy'}
          busy={busyId === detailListing.id}
          onAction={async () => {
            const isOwn = detailListing.sellerId === userId;
            if (isOwn) {
              await handleCancel(detailListing.id);
            } else {
              await handleBuy(detailListing.id);
            }
            setDetailListing(null);
          }}
          onClose={() => setDetailListing(null)}
        />
      )}
    </div>
  );
}
